import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Eye } from "lucide-react";
import toast from "react-hot-toast";
import {
  Button,
  Input,
  Table,
  Pagination,
  Card,
  ConfirmModal,
  Badge,
} from "@/components/ui";
import { clientsApi } from "@/services/api";

export default function ClientsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["clients", { search, page }],
    queryFn: () => clientsApi.list({ search, page, per_page: 10 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientsApi.delete(id),
    onSuccess: () => {
      toast.success("Cliente removido com sucesso");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setDeleteId(null);
    },
    onError: () => {
      toast.error("Erro ao remover cliente");
    },
  });

  const clients = data?.data?.items || [];
  const total = data?.data?.total || 0;
  const totalPages = Math.ceil(total / 10);

  const columns = [
    {
      key: "name",
      header: "Nome",
      render: (client: any) => (
        <div>
          <p className="font-medium">{client.name}</p>
          {client.trade_name && (
            <p className="text-sm text-text-muted">{client.trade_name}</p>
          )}
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
    },
    {
      key: "document",
      header: "Documento",
      render: (client: any) => (
        <span className="font-mono text-sm">{client.document || "-"}</span>
      ),
    },
    {
      key: "budgets_count",
      header: "Orçamentos",
      render: (client: any) => (
        <Badge variant="neutral">{client.budgets_count || 0}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-20",
      render: (client: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/clients/${client.id}`);
            }}
            className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/clients/${client.id}/edit`);
            }}
            className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteId(client.id);
            }}
            className="p-2 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
            Clientes
          </h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">
            Gerencie seus clientes
          </p>
        </div>
        <Link to="/admin/clients/new" className="w-full sm:w-auto">
          <Button
            leftIcon={<Plus className="w-5 h-5" />}
            className="w-full sm:w-auto"
          >
            Novo Cliente
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Buscar por nome, email ou documento..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              leftIcon={<Search className="w-5 h-5" />}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Table
        columns={columns}
        data={clients}
        keyExtractor={(client) => client.id}
        isLoading={isLoading}
        emptyMessage="Nenhum cliente encontrado"
        onRowClick={(client) => navigate(`/admin/clients/${client.id}`)}
      />

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={total}
        itemsPerPage={10}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Remover Cliente"
        message="Tem certeza que deseja remover este cliente? Esta ação não pode ser desfeita."
        confirmText="Remover"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
